import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getRoomsDirect, deleteRoomDirect } from '@timetable/api';
import { useAuthStore } from '../../store/authStore';
import { queryKeys } from '../../lib/queryKeys';
import { LoadingSpinner } from '../LoadingSpinner';
import { EmptyState } from '../EmptyState';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../Toast';
import { RoomFormDialog } from './RoomFormDialog';
import type { Room } from '@timetable/types';

export function RoomsTab() {
  const queryClient = useQueryClient();
  const schoolContext = useAuthStore((state) => state.schoolContext);
  const { showToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState<Room | null>(null);

  const filters = { school_id: schoolContext || undefined };

  const { data: rooms = [], isLoading, error } = useQuery({
    queryKey: queryKeys.rooms.list(filters),
    queryFn: () => getRoomsDirect(filters),
    enabled: !!schoolContext,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRoomDirect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rooms.all });
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
      showToast('Room deleted successfully', 'success');
    },
    onError: (err: Error) => {
      showToast(err.message || 'Failed to delete room', 'error');
      setDeleteDialogOpen(false);
      setRoomToDelete(null);
    },
  });

  const handleAddClick = () => {
    setSelectedRoom(null);
    setFormOpen(true);
  };

  const handleEditClick = (room: Room) => {
    setSelectedRoom(room);
    setFormOpen(true);
  };

  const handleDeleteClick = (room: Room) => {
    setRoomToDelete(room);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (roomToDelete) deleteMutation.mutate(roomToDelete.id);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedRoom(null);
  };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return <Alert severity="error">Failed to load rooms: {(error as Error).message}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
          Add Room
        </Button>
      </Box>

      {rooms.length === 0 ? (
        <EmptyState
          title="No rooms found"
          description="Get started by adding your first room"
        />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id} hover>
                  <TableCell>
                    <Typography variant="body1">{room.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {room.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {room.capacity != null ? room.capacity : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEditClick(room)} title="Edit room">
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(room)}
                      title="Delete room"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <RoomFormDialog open={formOpen} room={selectedRoom} onClose={handleFormClose} />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Room"
        message={`Are you sure you want to delete "${roomToDelete?.name}"? This action cannot be undone. If this room is assigned to any timetable records, the deletion will fail.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteDialogOpen(false); setRoomToDelete(null); }}
        severity="error"
      />
    </Box>
  );
}
